/**
 * One widget's data: fetched on mount and every `REFRESH_MS`, only the newest
 * reply drawn — the Buckets Brief's rule, for its reason (a slow, older reply
 * landing after a toggle drew the source that was switched away from).
 *
 * Two widgets asking the same thing at the same moment share one request: the
 * board may hold a widget twice (decision 1), and both mount and tick together.
 */
import { useEffect, useRef, useState } from "react";
import { REFRESH_MS } from "../../lib/brief-board";

const inFlight = new Map<string, Promise<unknown>>();

/** One request per key while it is in flight; the next tick asks again. */
function shared<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
  const running = inFlight.get(key) as Promise<T> | undefined;
  if (running) return running;
  const p = fetcher().finally(() => inFlight.delete(key));
  inFlight.set(key, p);
  return p;
}

export interface BriefData<T> {
  data: T | null;
  error: string | null;
  /** True until the first reply for the current key, success or failure. */
  loading: boolean;
}

/**
 * `key` names the request (endpoint and arguments); a new key fetches at once
 * and keeps drawing the old data until its own reply lands. `null` fetches nothing.
 */
export function useBriefData<T>(key: string | null, fetcher: () => Promise<T>): BriefData<T> {
  const [state, setState] = useState<BriefData<T>>({ data: null, error: null, loading: key !== null });
  const latest = useRef(0);
  // The fetcher closes over the key's arguments; the key alone decides when to refetch.
  const fetchRef = useRef(fetcher);
  fetchRef.current = fetcher;

  useEffect(() => {
    if (key === null) {
      setState({ data: null, error: null, loading: false });
      return;
    }
    let alive = true;
    const load = () => {
      const ticket = ++latest.current;
      shared(key, () => fetchRef.current())
        .then((data) => {
          if (alive && ticket === latest.current) setState({ data, error: null, loading: false });
        })
        .catch((e: unknown) => {
          if (!alive || ticket !== latest.current) return;
          setState((s) => ({ ...s, error: e instanceof Error ? e.message : String(e), loading: false }));
        });
    };
    setState((s) => ({ ...s, loading: true }));
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [key]);

  return state;
}
